import { useParams } from 'react-router-dom';
import { useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLeague } from '../../features/leagues/api';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatPoints } from '../../lib/dates';
import { AppLayout } from '../../components/AppLayout';
import { LeagueNav } from './LeagueNav';
import { api } from '../../lib/api';

interface HistoryStats {
  played: number;
  totalPoints: number;
  averagePoints: number;
  best: { number: number; total: number } | null;
  worst: { number: number; total: number } | null;
  fourOfFour: number;
  exactHits: number;
  overHits: number;
  bonusTotal: number;
  penaltyTotal: number;
  originalityTotal: number;
  averagePosition: number | null;
}

interface HistoryRow {
  matchdayId: string;
  number: number;
  total: number;
  base: number;
  exactResult: number;
  over: number;
  originality: number;
  bonus: number;
  penalty: number;
  breakdown: Array<{ type: string; label: string; points: number }>;
  positionAfter: number | null;
}

/**
 * Storico personale (lineeguida §38): ogni giornata pubblicata con il
 * dettaglio completo del punteggio, espandibile.
 */
export function HistoryPage() {
  const { id = '' } = useParams();
  const league = useLeague(id);
  usePageTitle(league.data ? `Storico — ${league.data.name}` : 'Storico');
  const [expanded, setExpanded] = useState<string | null>(null);

  const historyQuery = useQuery({
    queryKey: ['leagues', id, 'history'],
    queryFn: () => api<{ rows: HistoryRow[]; stats: HistoryStats }>(`/leagues/${id}/history`),
    enabled: !!id,
  });
  const history = { ...historyQuery, data: historyQuery.data?.rows };
  const stats = historyQuery.data?.stats;

  return (
    <AppLayout title={league.data ? `Il mio storico — ${league.data.name}` : 'Storico'}>
      <LeagueNav leagueId={id} />

      {history.isLoading && (
        <p role="status" className="mt-6">Caricamento storico…</p>
      )}

      {history.data && history.data.length === 0 && (
        <p className="mt-6 text-lg">
          Il tuo storico apparirà dopo la pubblicazione della prima giornata.
        </p>
      )}

      {history.data && history.data.length > 0 && (
        <>
          {/* Statistiche riassuntive testuali (lineeguida §37) */}
          {stats && (
            <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {(
                [
                  ['Punti totali', formatPoints(stats.totalPoints)],
                  ['Media punti a giornata', formatPoints(stats.averagePoints)],
                  ['Posizione media', stats.averagePosition != null ? `${stats.averagePosition}º` : '—'],
                  ['Giornate giocate', String(stats.played)],
                  [
                    'Miglior giornata',
                    stats.best ? `${formatPoints(stats.best.total)} (${stats.best.number}ª)` : '—',
                  ],
                  [
                    'Peggior giornata',
                    stats.worst ? `${formatPoints(stats.worst.total)} (${stats.worst.number}ª)` : '—',
                  ],
                  ['Bonus tutte corrette', String(stats.fourOfFour)],
                  ['Risultati esatti indovinati', String(stats.exactHits)],
                  ['OVER riusciti', String(stats.overHits)],
                  ['Punti da bonus', formatPoints(stats.bonusTotal)],
                  ['Punti da malus', formatPoints(stats.penaltyTotal)],
                  ['Punti da originalità', formatPoints(stats.originalityTotal)],
                ] as Array<[string, string]>
              ).map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-ink-200 p-4 dark:border-ink-800">
                  <dt className="text-sm text-ink-600 dark:text-ink-300">{label}</dt>
                  <dd className="text-2xl font-bold">{value}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="mt-6 overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <caption className="visually-hidden">
                Storico delle tue giornate: numero, punti e posizione in classifica, con dettaglio espandibile
              </caption>
              <thead>
                <tr className="border-b-2 border-ink-300 dark:border-ink-600">
                  <th scope="col" className="py-2 pr-3">Giornata</th>
                  <th scope="col" className="py-2 pr-3 text-right">Punti</th>
                  <th scope="col" className="py-2 pr-3 text-right">Posizione</th>
                  <th scope="col" className="py-2">Dettaglio</th>
                </tr>
              </thead>
              <tbody>
                {history.data.map((row) => (
                  <Fragment key={row.matchdayId}>
                    <tr className="border-b border-ink-200 dark:border-ink-800">
                      <th scope="row" className="py-3 pr-3 font-medium">Giornata {row.number}</th>
                      <td className="py-3 pr-3 text-right text-lg font-bold">{formatPoints(row.total)}</td>
                      <td className="py-3 pr-3 text-right">{row.positionAfter ? `${row.positionAfter}º` : '—'}</td>
                      <td className="py-3">
                        <button
                          type="button"
                          className="min-h-11 rounded-lg px-3 py-2 font-medium text-panda-700 underline dark:text-panda-300"
                          aria-expanded={expanded === row.matchdayId}
                          onClick={() => setExpanded(expanded === row.matchdayId ? null : row.matchdayId)}
                        >
                          {expanded === row.matchdayId ? 'Nascondi' : 'Vedi il calcolo'}
                          <span className="visually-hidden"> della giornata {row.number}</span>
                        </button>
                      </td>
                    </tr>
                    {expanded === row.matchdayId && (
                      <tr className="border-b border-ink-200 dark:border-ink-800">
                        <td colSpan={4} className="bg-ink-50 px-4 py-3 dark:bg-ink-900">
                          <h3 className="font-semibold">Dettaglio del calcolo — Giornata {row.number}</h3>
                          <ul className="mt-2 flex flex-col gap-1">
                            {row.breakdown.map((entry, i) => (
                              <li key={i}>{entry.label}</li>
                            ))}
                            <li className="mt-1 border-t border-ink-300 pt-2 font-bold dark:border-ink-600">
                              Totale: {formatPoints(row.total)}
                            </li>
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AppLayout>
  );
}
